import {
  Container,
  TableHead,
  Paper,
  Table,
  TableContainer,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Box,
  Chip,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import NavigationHeader from "../../components/NavigationHeader";
import { staffAttendanceApi } from "./masterApiService";
import {
  commonButtonProps,
  commonDateFieldProps,
  commonTextFieldProps,
  smallButtonProp,
  tableCellStyles,
  tableHeaderStyles,
} from "../../utils/styles";
import { Loading } from "../../components/Loading";
import { useToast } from "../../components/Toast";
import { useNavigate } from "react-router-dom";
import { formatText } from "../../utils/utils";

interface Staff {
  id: string;
  companyId: string;
  name: string;
  contactNumber: string;
  alternateNumber: string;
  address: string;
  type: string;
  status: string;
  employmentType: string;
  joiningDate: string;
  salary: number;
  aadharNumber: string;
  drivingLicense: string | null;
  referredByName: string;
  referredByContact: string;
  reference1Name: string;
  reference1Contact: string;
  reference2Name: string;
  reference2Contact: string;
  photoUrl: string | null;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

interface AttendanceRecord {
  staffId: string;
  date: string;
  loginTime: string | null;
  logoutTime: string | null;
  status: "PRESENT" | "ABSENT" | "HALF_DAY" | "HOLIDAY";
  notes: string;
  isNew?: boolean;
  isEdited?: boolean;
  staffMember: Staff;
}

const StaffAttendance = () => {
  const [staff, setStaff] = useState<Staff[]>([]);
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();
  const [isHolidayMarked, setIsHolidayMarked] = useState(false);
  const navigate = useNavigate();

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const attendanceResponse = await staffAttendanceApi.getAttendanceByDate(
        selectedDate
      );
      if (attendanceResponse) {
        setAttendance(attendanceResponse);
      }
    } catch (error) {
      console.error("Error fetching staff:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();
  }, [selectedDate]);

  const handleMarkHoliday = () => {
    const updated = attendance.map((record) => ({
      ...record,
      status: (isHolidayMarked
        ? "PRESENT"
        : "HOLIDAY") as AttendanceRecord["status"],
      isEdited: true,
    }));
    setAttendance(updated);
    setIsHolidayMarked((prev) => !prev);
  };

  const handleSubmitAttendance = async () => {
    setIsSubmitting(true);
    try {
      const recordToCreate = attendance.filter((record) => record.isNew);
      const recordToUpdate = attendance.filter(
        (record) => record.isEdited && !record.isNew
      );
      let promises = [];

      if (recordToCreate.length > 0) {
        promises.push(staffAttendanceApi.createAttendance(recordToCreate));
      }
      if (recordToUpdate.length > 0) {
        promises.push(staffAttendanceApi.updateAttendance(recordToUpdate));
      }

      await Promise.all(promises);

      showToast("Attendance saved successfully", "success");
      navigate(-1);
    } catch (error) {
      showToast("Error saving attendance", "error");
      console.error("Error creating attendance:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAttendanceChange = (
    staffId: string,
    field: keyof AttendanceRecord,
    value: string
  ) => {
    setAttendance((prev) =>
      prev.map((record) =>
        record.staffId === staffId
          ? { ...record, [field]: value, isEdited: true }
          : record
      )
    );
  };

  if (loading || isSubmitting) {
    return (
      <Loading
        message={isSubmitting ? "Saving..." : "Loading data..."}
        fullScreen={true}
      />
    );
  }

  return (
    <Container sx={{ mt: 2, mb: 4 }}>
      <NavigationHeader
        breadcrumbs={[
          { label: "Master Data", path: "/master-data" },
          { label: "Work Force", path: "/master-data/staff/DRIVER" },
          { label: "Attendance" },
        ]}
        title={"Attendance"}
      />

      <Box
        sx={{
          mb: 3,
          display: "flex",
          gap: 2,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <TextField
          label="Date"
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          inputProps={{
            max: new Date().toISOString().split("T")[0], // today's date
          }}
          {...commonDateFieldProps}
        />

        <Button
          variant="contained"
          color="primary"
          onClick={handleMarkHoliday}
          {...commonButtonProps}
        >
          {isHolidayMarked ? "Unmark Holiday" : "Mark Holiday"}
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead sx={{ ...tableHeaderStyles }}>
            <TableRow>
              <TableCell sx={{ ...tableCellStyles }}>Staff</TableCell>
              <TableCell sx={{ ...tableCellStyles }}>Type</TableCell>
              <TableCell sx={{ ...tableCellStyles }}>Status</TableCell>
              <TableCell sx={{ ...tableCellStyles }}>In Time</TableCell>
              <TableCell sx={{ ...tableCellStyles }}>Out Time</TableCell>
              <TableCell sx={{ ...tableCellStyles }}>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {attendance.map((record) => {
              return (
                <TableRow key={record.staffId} hover>
                  <TableCell sx={{ ...tableCellStyles, fontWeight: "bold" }}>
                    {record?.staffMember?.name} {record?.isNew && "*"}
                    {record?.staffMember?.contactNumber ? (
                      <span
                        style={{
                          fontSize: "0.65rem",
                          color: "text.secondary",
                          fontWeight: "normal",
                        }}
                      >
                        ` (${record?.staffMember?.contactNumber})`
                      </span>
                    ) : (
                      ""
                    )}
                  </TableCell>
                  <TableCell sx={{ ...tableCellStyles }}>
                    {formatText(record?.staffMember?.type || "N/A")}
                  </TableCell>
                  <TableCell sx={{ ...tableCellStyles }}>
                    <FormControl {...commonTextFieldProps}>
                      <Select
                        value={record.status}
                        onChange={(e: SelectChangeEvent) =>
                          handleAttendanceChange(
                            record.staffId,
                            "status",
                            e.target.value
                          )
                        }
                        sx={{
                          fontSize: "0.75rem",
                          height: "30px",
                        }}
                        size="small"
                      >
                        <MenuItem sx={{ fontSize: "0.75rem" }} value="PRESENT">
                          Present
                        </MenuItem>
                        <MenuItem sx={{ fontSize: "0.75rem" }} value="ABSENT">
                          Absent
                        </MenuItem>
                        <MenuItem sx={{ fontSize: "0.75rem" }} value="HALF_DAY">
                          Half Day
                        </MenuItem>
                        <MenuItem sx={{ fontSize: "0.75rem" }} value="HOLIDAY">
                          Holiday
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={{ ...tableCellStyles }}>
                    <TextField
                      type="time"
                      value={record.loginTime}
                      onChange={(e) =>
                        handleAttendanceChange(
                          record.staffId,
                          "loginTime",
                          e.target.value
                        )
                      }
                      variant="standard"
                      {...commonDateFieldProps}
                    />
                  </TableCell>
                  <TableCell sx={{ ...tableCellStyles }}>
                    <TextField
                      type="time"
                      value={record.logoutTime}
                      onChange={(e) =>
                        handleAttendanceChange(
                          record.staffId,
                          "logoutTime",
                          e.target.value
                        )
                      }
                      variant="standard"
                      {...commonDateFieldProps}
                    />
                  </TableCell>
                  <TableCell sx={{ ...tableCellStyles }}>
                    <TextField
                      placeholder="Add notes..."
                      value={record.notes}
                      onChange={(e) =>
                        handleAttendanceChange(
                          record.staffId,
                          "notes",
                          e.target.value
                        )
                      }
                      variant="standard"
                      {...commonTextFieldProps}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmitAttendance}
          {...commonButtonProps}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
      </Box>
    </Container>
  );
};

export default StaffAttendance;
